import React, { useState, useEffect } from 'react';
import { MessageCircle, RefreshCw, Loader2, Users } from 'lucide-react';
import { instagramPostsAPI } from '../../../services/api';
import { useTheme } from '../../../contexts/ThemeContext';
import { Skeleton, SkeletonLinhaContato } from '../../../components/Skeleton';

const TopCommentersConsolidated = ({ clienteId, refreshTrigger }) => {
  const { darkMode } = useTheme();
  const [topCommenters, setTopCommenters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (!clienteId) return;
    loadData();
  }, [clienteId, refreshTrigger]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await instagramPostsAPI.getTopCommentersConsolidated(clienteId);
      setTopCommenters(res.data);
    } catch (e) {
      console.error('Erro ao carregar top comentadores:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleImportAll = async () => {
    setImporting(true);
    try {
      await Promise.all([
        instagramPostsAPI.importAllComments(clienteId),
        instagramPostsAPI.importFacebookComments(clienteId),
      ]);
      await loadData();
    } catch (e) {
      console.error(e);
    } finally {
      setImporting(false);
    }
  };

  if (loading) return (
    <div className={`rounded-xl p-4 sm:p-6 ${darkMode ? 'bg-zinc-800' : 'bg-white'} shadow`}>
      <Skeleton className="h-5 w-1/3 mb-4" />
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <SkeletonLinhaContato key={i} />
        ))}
      </div>
    </div>
  );

  const cardBg = darkMode ? 'bg-zinc-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-gray-400' : 'text-gray-500';

  const getPlatformBadge = (platforms) => {
    if (!platforms) return null;
    const list = platforms.split(', ');
    return (
      <div className="flex gap-1">
        {list.includes('instagram') && <span className="text-xs px-1.5 py-0.5 rounded bg-pink-500/20 text-pink-500">IG</span>}
        {list.includes('facebook') && <span className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-500">FB</span>}
      </div>
    );
  };

  return (
    <div className={`rounded-xl p-4 sm:p-6 ${cardBg} shadow`}>
      <div className="flex items-center justify-between mb-3 sm:mb-4 gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="w-4 h-4 sm:w-5 sm:h-5 text-purple-500 flex-shrink-0" />
          <h3 className={`text-sm sm:text-base font-semibold truncate ${textPrimary}`}><span className="sm:hidden">Top Comentadores</span><span className="hidden sm:inline">Top Comentadores (Todas as Redes)</span></h3>
        </div>
        <button
          onClick={handleImportAll}
          disabled={importing}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition ${
            darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Sincronizar Tudo
        </button>
      </div>

      {!topCommenters?.length ? (
        <p className={`text-sm ${textSecondary}`}>
          Nenhum comentário importado ainda. Clique em "Sincronizar Tudo" para importar comentários do Instagram e Facebook.
        </p>
      ) : (
        <div className="space-y-1.5 sm:space-y-2">
          {topCommenters.map((c, i) => (
            <div
              key={c.username}
              className={`flex items-center gap-2 sm:gap-3 p-2 sm:p-2.5 rounded-lg ${darkMode ? 'bg-zinc-900/50' : 'bg-gray-50'}`}
            >
              <span className={`w-5 sm:w-6 text-center text-xs sm:text-sm font-bold flex-shrink-0 ${i < 3 ? 'text-purple-500' : textSecondary}`}>
                {i + 1}
              </span>
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-[10px] sm:text-xs font-bold flex-shrink-0">
                {c.username?.charAt(0)?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${textPrimary}`}>{c.username}</p>
                {getPlatformBadge(c.platforms)}
              </div>
              <div className="flex items-center gap-1.5 sm:gap-3 text-xs flex-shrink-0">
                <span className={`font-semibold ${textPrimary}`}>{c.total} 💬</span>
                {Number(c.positivos) > 0 && <span className="hidden sm:inline text-green-500">{c.positivos} 😊</span>}
                {Number(c.negativos) > 0 && <span className="hidden sm:inline text-red-500">{c.negativos} 😞</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TopCommentersConsolidated;
